'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { ambassadorService } from '@/lib/services/ambassadorService';

interface AmbassadorApplicationModalProps {
  onSubmit: () => void;
  onCancel: () => void;
}

const AmbassadorApplicationModal: React.FC<AmbassadorApplicationModalProps> = ({
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    skills: [] as string[],
    backgroundInfo: '',
    experience: '',
    motivation: '',
    availability: '',
    institutionalInterests: [] as string[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const skillOptions = [
    'Public Speaking',
    'Social Media',
    'Event Planning',
    'Writing',
    'Translation',
    'Graphic Design',
    'Video Production',
    'Community Organizing',
    'Fundraising',
    'Teaching',
  ];

  const institutionalOptions = [
    { id: 'universities', label: 'Universities & Colleges' },
    { id: 'schools', label: 'Schools (K-12)' },
    { id: 'religious_organizations', label: 'Religious Organizations' },
    { id: 'community_centers', label: 'Community Centers' },
    { id: 'ngos', label: 'NGOs & Nonprofits' },
    { id: 'government_agencies', label: 'Government Agencies' },
    { id: 'corporate_partners', label: 'Corporate Partners' },
    { id: 'media_outlets', label: 'Media Outlets' },
  ];

  const handleSkillToggle = (skill: string) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter((s) => s !== skill)
        : [...prev.skills, skill],
    }));
  };

  const handleInstitutionalToggle = (institutionId: string) => {
    setFormData((prev) => ({
      ...prev,
      institutionalInterests: prev.institutionalInterests.includes(institutionId)
        ? prev.institutionalInterests.filter((i) => i !== institutionId)
        : [...prev.institutionalInterests, institutionId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.skills.length === 0) {
      setSubmitError('Please select at least one skill');
      return;
    }

    if (formData.institutionalInterests.length === 0) {
      setSubmitError('Please select at least one institutional interest');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await ambassadorService.submitApplication({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        location: formData.location,
        skills: formData.skills,
        backgroundInfo: formData.backgroundInfo,
        experience: formData.experience,
        motivation: formData.motivation,
        availability: formData.availability,
        institutionalInterests: formData.institutionalInterests,
      });

      if (result) {
        alert(
          'Ambassador Application Submitted Successfully!\n\nThank you for your interest in becoming a SIG Day Ambassador. Our team will review your application and contact you within 5-7 business days.\n\nYou will receive a confirmation email shortly.'
        );
        onSubmit();
      } else {
        setSubmitError('Failed to submit application. Please try again.');
      }
    } catch (error: any) {
      setSubmitError('An error occurred. Please try again later.');
      console.error('Ambassador application submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6 overflow-y-auto">
      <div className="bg-card rounded-lg max-w-4xl w-full p-8 relative my-8">
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-muted/80 transition-colors"
          aria-label="Close modal"
        >
          <Icon name="XMarkIcon" size={24} className="text-foreground" />
        </button>

        <div className="mb-6">
          <h2 className="text-3xl font-headline font-bold text-foreground mb-2">
            Ambassador Application
          </h2>
          <p className="text-muted-foreground">
            Join our global network of peace ambassadors and help spread the SIG Day movement
            worldwide.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-foreground mb-2">
                Full Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                id="fullName"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address <span className="text-destructive">*</span>
              </label>
              <input
                type="email"
                id="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                Phone Number <span className="text-destructive">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-foreground mb-2">
                Location <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                id="location"
                required
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="City, Country"
              />
            </div>
          </div>

          {/* Skills Section */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Your Skills (Select all that apply) <span className="text-destructive">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {skillOptions.map((skill) => (
                <button
                  key={skill}
                  type="button"
                  onClick={() => handleSkillToggle(skill)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    formData.skills.includes(skill)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  {skill}
                </button>
              ))}
            </div>
          </div>

          {/* Background Information */}
          <div>
            <label
              htmlFor="backgroundInfo"
              className="block text-sm font-medium text-foreground mb-2"
            >
              Background Information <span className="text-destructive">*</span>
            </label>
            <textarea
              id="backgroundInfo"
              required
              value={formData.backgroundInfo}
              onChange={(e) => setFormData({ ...formData, backgroundInfo: e.target.value })}
              rows={4}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              placeholder="Share your educational background, professional experience, and relevant qualifications..."
            />
          </div>

          {/* Institutional Interests */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Institutional Interests <span className="text-destructive">*</span>
            </label>
            <p className="text-sm text-muted-foreground mb-3">
              Select the types of institutions you would like to engage with as an ambassador:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {institutionalOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleInstitutionalToggle(option.id)}
                  className={`px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 text-left ${
                    formData.institutionalInterests.includes(option.id)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Experience */}
          <div>
            <label htmlFor="experience" className="block text-sm font-medium text-foreground mb-2">
              Relevant Experience <span className="text-destructive">*</span>
            </label>
            <textarea
              id="experience"
              required
              value={formData.experience}
              onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
              rows={4}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              placeholder="Tell us about your experience in community organizing, peace-building, or related fields..."
            />
          </div>

          {/* Motivation */}
          <div>
            <label htmlFor="motivation" className="block text-sm font-medium text-foreground mb-2">
              Why do you want to be an ambassador? <span className="text-destructive">*</span>
            </label>
            <textarea
              id="motivation"
              required
              value={formData.motivation}
              onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
              rows={4}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              placeholder="Share your motivation and vision for spreading the SIG Day (The Global No Enemy Festival) movement..."
            />
          </div>

          {/* Time Commitment */}
          <div>
            <label htmlFor="availability" className="block text-sm font-medium text-foreground mb-2">
              Time Commitment <span className="text-destructive">*</span>
            </label>
            <select
              id="availability"
              required
              value={formData.availability}
              onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select your availability</option>
              <option value="2-5">2-5 hours per week</option>
              <option value="5-10">5-10 hours per week</option>
              <option value="10-20">10-20 hours per week</option>
              <option value="20+">20+ hours per week</option>
            </select>
          </div>

          {submitError && (
            <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md text-sm">
              {submitError}
            </div>
          )}

          <div className="flex items-center space-x-4 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-md font-cta font-bold transition-all duration-300 hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 bg-muted text-foreground rounded-md font-medium transition-all duration-300 hover:bg-muted/80"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AmbassadorApplicationModal;